import { TABLET_API_ORIGIN } from '@/global/env';
import { RestClientInterface } from '@/lib/restClient';
import {
  StoreManagementDeliveryStoreCreateParams,
  StoreManagementDeliveryStoreCreateResponse,
} from '@/services/StoreManagement/DeliveryStores/hooks/create/useStoreManagementDeliveryStoreCreate';
import {
  StoreManagementDeliveryStoreSearchListParams,
  StoreManagementDeliveryStoreSearchListResponse,
} from '@/services/StoreManagement/DeliveryStores/hooks/searchList/useStoreManagementDeliveryStoreSearchList';
import {
  StoreManagementDeliveryStoreUpdateParams,
  StoreManagementDeliveryStoreUpdateResponse,
} from '@/services/StoreManagement/DeliveryStores/hooks/update/useStoreManagementDeliveryStoreUpdate';
import { StoreManagementDeliveryStoreUpdateLinkedStatusParams } from '@/services/StoreManagement/DeliveryStores/hooks/updateLinkedStatus/useStoreManagementDeliveryStoreUpdateLinkedStatus';

interface Interface {
  create: (
    params: StoreManagementDeliveryStoreCreateParams
  ) => Promise<StoreManagementDeliveryStoreCreateResponse>;
  update: (
    id: string,
    params: StoreManagementDeliveryStoreUpdateParams
  ) => Promise<StoreManagementDeliveryStoreUpdateResponse>;
  updateLinkedStatus: (
    id: string,
    params: StoreManagementDeliveryStoreUpdateLinkedStatusParams
  ) => Promise<void>;
  searchList: (
    params: StoreManagementDeliveryStoreSearchListParams
  ) => Promise<StoreManagementDeliveryStoreSearchListResponse>;
}

export class StoreManagementDeliveryStoreService implements Interface {
  constructor(private readonly restClient: RestClientInterface) {}

  public async create(
    params: StoreManagementDeliveryStoreCreateParams
  ): Promise<StoreManagementDeliveryStoreCreateResponse> {
    return await this.restClient.post<
      StoreManagementDeliveryStoreCreateParams,
      StoreManagementDeliveryStoreCreateResponse
    >(`${TABLET_API_ORIGIN}/dashboard-api/delivery-stores` as const, params);
  }

  public async update(
    id: string,
    params: StoreManagementDeliveryStoreUpdateParams
  ): Promise<StoreManagementDeliveryStoreUpdateResponse> {
    return await this.restClient.patch(
      `${TABLET_API_ORIGIN}/dashboard-api/delivery-stores/${id}` as const,
      params
    );
  }

  public async updateLinkedStatus(
    id: string,
    params: StoreManagementDeliveryStoreUpdateLinkedStatusParams
  ): Promise<void> {
    return await this.restClient.patch<
      StoreManagementDeliveryStoreUpdateLinkedStatusParams,
      void
    >(
      `${TABLET_API_ORIGIN}/dashboard-api/delivery-stores/${id}/linked-status` as const,
      params
    );
  }

  public async searchList(
    params: StoreManagementDeliveryStoreSearchListParams
  ): Promise<StoreManagementDeliveryStoreSearchListResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('limit', String(params.limit));
    queryParams.append('current_page', String(params.currentPage));
    if (params.storeName !== '')
      queryParams.append('store_name', String(params.storeName));
    params.authStoreIds.forEach((authStoreId) => {
      queryParams.append('auth_store_ids', String(authStoreId));
    });
    params.pendingReasons.forEach((pendingReason) => {
      queryParams.append('pending_reasons', pendingReason);
    });
    if (params.storeState !== '') {
      queryParams.append('store_state', String(params.storeState));
    }
    return await this.restClient.get<StoreManagementDeliveryStoreSearchListResponse>(
      `${TABLET_API_ORIGIN}/dashboard-api/delivery-stores?${queryParams.toString()}` as const
    );
  }
}
